import { useNavigate } from '@tanstack/react-router'
import { type PlayCreate, formatOrdinal, tiedRanks } from '@topmeeps/shared'
import { useCreatePlay, useGames } from '../../api/hooks'
import { formatDateTime } from '../../datetime'
import {
  GameThumb,
  MeepleAvatar,
  NumberCircle,
  ScorePad,
  ScorePadRow,
  Stamp,
} from '../../primitives'
import { useRecordPlayDraft } from '../../stores/recordPlayDraft'

export const RecordConfirmStep = () => {
  const navigate = useNavigate()
  const draft = useRecordPlayDraft()
  const games = useGames()
  const createPlay = useCreatePlay()
  const game = games.data?.find((g) => g.id === draft.gameId)

  const onSave = async () => {
    if (!draft.gameId) return
    const payload: PlayCreate = {
      gameId: draft.gameId,
      groupId: draft.groupId,
      playedAt: draft.playedAt,
      durationMinutes: draft.durationMinutes,
      location: draft.location || null,
      notes: draft.notes || null,
      expansions: null,
      participants: draft.participants.map((p) => ({
        userId: p.userId,
        guestId: p.guestId,
        rank: p.rank,
        score: p.score,
        outcomeTags: p.outcomeTags,
        subscores: p.subscores,
      })),
    }
    const result = await createPlay.mutateAsync(payload)
    draft.reset(draft.groupId)
    // `replace` drops this Confirm step from history; `recorded` tells
    // the play screen to show its "just recorded" treatment.
    navigate({
      to: '/play/$id',
      params: { id: result.playId },
      search: { recorded: true },
      replace: true,
    })
  }

  if (!game) {
    return <div style={{ color: 'var(--ink-3)' }}>Pick a game first.</div>
  }

  const ordered = [...draft.participants].sort((a, b) => a.rank - b.rank)
  const tied = tiedRanks(ordered.map((p) => p.rank))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        className="felt-card"
        style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 12 }}
      >
        <GameThumb label={game.name} imageUrl={game.imageUrl} size={48} />
        <div style={{ flex: 1 }}>
          <div className="kicker">recording</div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18 }}>
            {game.name}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            {formatDateTime(draft.playedAt)}
            {draft.location ? ` · ${draft.location}` : ''}
          </div>
        </div>
        <Stamp>draft</Stamp>
      </div>

      <ScorePad>
        {ordered.map((p, i) => (
          <ScorePadRow key={`${p.userId ?? p.guestId ?? p.displayName}-${i}`}>
            <NumberCircle n={p.rank} winner={p.rank === 1} size={24} />
            <MeepleAvatar name={p.displayName} hue={p.avatarHue} size={28} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{p.displayName}</span>
              {tied.has(p.rank) && (
                <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  Tied for {formatOrdinal(p.rank)}
                </span>
              )}
            </div>
            {p.score != null && (
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink-2)' }}>
                {p.score}
              </div>
            )}
          </ScorePadRow>
        ))}
      </ScorePad>

      {draft.notes && (
        <div className="felt-card" style={{ padding: 12 }}>
          <div className="kicker">notes</div>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', whiteSpace: 'pre-wrap' }}>
            {draft.notes}
          </div>
        </div>
      )}

      <button type="button" className="btn-stamp" onClick={onSave} disabled={createPlay.isPending}>
        {createPlay.isPending ? 'Saving…' : 'Save play'}
      </button>
      {createPlay.isError && (
        <div style={{ color: 'var(--terra-ink)', fontSize: 12 }}>
          {(createPlay.error as Error).message}
        </div>
      )}
    </div>
  )
}
